import type { CargaDeLotes } from '../../api/lotes.js';
import type { TipoDeLote } from '../../api/sincronizacao.js';
import type { CargaInicial, EtapaDaCarga, StatusDaCarga } from '../../api/sincronizacao.js';
import { ETAPAS_DA_CARGA, INTERVALO_MAXIMO, INTERVALO_MINIMO } from '../../api/sincronizacao.js';
import type { TomStatus } from '../../componentes/ChipStatus.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';

/**
 * Como a tela de Sincronização lê o estado da carga inicial, das etapas e da
 * configuração da periódica. Tudo aqui é função pura, testada à parte.
 */

const ROTULO_DO_STATUS: Readonly<Record<StatusDaCarga, string>> = {
  NaoIniciada: 'Não iniciada',
  EmAndamento: 'Em andamento',
  Concluida: 'Concluída',
  Falhou: 'Falhou',
};

const TOM_DO_STATUS: Readonly<Record<StatusDaCarga, TomStatus>> = {
  NaoIniciada: 'desativado',
  EmAndamento: 'andamento',
  Concluida: 'concluida',
  Falhou: 'falha',
};

export function rotuloDoStatusDaCarga(status: StatusDaCarga): string {
  return ROTULO_DO_STATUS[status];
}

export function tomDoStatusDaCarga(status: StatusDaCarga): TomStatus {
  return TOM_DO_STATUS[status];
}

export type AcaoDaCarga = 'iniciar' | 'retomar';

/**
 * O botão que a carga oferece agora, ou nenhum.
 *
 * `EmAndamento` sem execução é carga interrompida (o servidor reiniciou): a
 * API a retoma do checkpoint no próximo disparo, então o botão é "Retomar".
 * Concluída não volta a rodar — é uma vez por armazém.
 */
export function acaoDaCarga(carga: CargaInicial): AcaoDaCarga | null {
  switch (carga.status) {
    case 'NaoIniciada':
      return 'iniciar';
    case 'Falhou':
      return 'retomar';
    case 'EmAndamento':
      return carga.emExecucao ? null : 'retomar';
    case 'Concluida':
      return null;
  }
}

export type EstadoDaEtapa = 'aguardando' | 'andamento' | 'concluida' | 'falhou' | 'pendente';

/**
 * Lotes depois da carga inicial concluída: a verdade é a carga de lotes
 * (`GET /sincronizacao/lotes/carga`), e não o status da carga inicial. Um
 * armazém cuja carga inicial terminou antes de a etapa Lotes existir nunca
 * leu lote nenhum — chamar isso de "Concluída" esconderia exatamente o que
 * falta fazer. Sem resposta da carga de lotes, a tela não afirma concluída.
 */
function estadoDosLotes(cargaDeLotes: CargaDeLotes | null): EstadoDaEtapa {
  if (cargaDeLotes === null) {
    return 'pendente';
  }
  if (cargaDeLotes.emExecucao) {
    return 'andamento';
  }
  if (cargaDeLotes.status === 'Falhou') {
    return 'falhou';
  }
  return cargaDeLotes.sincronizados ? 'concluida' : 'pendente';
}

/** A etapa em relação à atual: antes dela já terminou, depois ainda espera. */
export function estadoDaEtapa(
  carga: CargaInicial,
  etapa: EtapaDaCarga,
  cargaDeLotes: CargaDeLotes | null = null,
): EstadoDaEtapa {
  if (carga.status === 'Concluida') {
    return etapa === 'Lotes' ? estadoDosLotes(cargaDeLotes) : 'concluida';
  }

  if (carga.etapaAtual === null) {
    return 'aguardando';
  }

  const atual = ETAPAS_DA_CARGA.indexOf(carga.etapaAtual);
  const esta = ETAPAS_DA_CARGA.indexOf(etapa);

  if (esta < atual) {
    return 'concluida';
  }

  if (esta > atual) {
    return 'aguardando';
  }

  return carga.status === 'Falhou' ? 'falhou' : 'andamento';
}

const ROTULO_DO_ESTADO: Readonly<Record<EstadoDaEtapa, string>> = {
  aguardando: 'Aguardando',
  andamento: 'Em andamento',
  concluida: 'Concluída',
  falhou: 'Parou aqui',
  pendente: 'Não carregada',
};

const TOM_DO_ESTADO: Readonly<Record<EstadoDaEtapa, TomStatus>> = {
  aguardando: 'desativado',
  andamento: 'andamento',
  concluida: 'concluida',
  falhou: 'falha',
  pendente: 'divergencia',
};

export function rotuloDoEstadoDaEtapa(estado: EstadoDaEtapa): string {
  return ROTULO_DO_ESTADO[estado];
}

export function tomDoEstadoDaEtapa(estado: EstadoDaEtapa): TomStatus {
  return TOM_DO_ESTADO[estado];
}

export function processadosNaEtapa(
  carga: CargaInicial,
  etapa: EtapaDaCarga,
  cargaDeLotes: CargaDeLotes | null = null,
): number {
  switch (etapa) {
    case 'Categorias':
      return carga.categoriasProcessadas;
    case 'Produtos':
      return carga.produtosProcessados;
    case 'Saldos':
      return carga.saldosProcessados;
    case 'Lotes':
      // A carga avulsa de lotes conta à parte; vale o maior dos dois.
      return Math.max(carga.lotesProcessados, cargaDeLotes?.lotesProcessados ?? 0);
  }
}

const SEGUNDO = 1000;
const MINUTO = 60 * SEGUNDO;
const HORA = 60 * MINUTO;

/** "1 h 5 min", "3 min 20 s", "12 s" — a duas unidades, sem casa sobrando. */
export function formatarDuracao(ms: number | null): string {
  if (ms === null) {
    return '—';
  }

  const horas = Math.floor(ms / HORA);
  const minutos = Math.floor((ms % HORA) / MINUTO);
  const segundos = Math.floor((ms % MINUTO) / SEGUNDO);

  if (horas > 0) {
    return `${String(horas)} h ${String(minutos)} min`;
  }

  if (minutos > 0) {
    return `${String(minutos)} min ${String(segundos)} s`;
  }

  return `${String(segundos)} s`;
}

export const ERRO_DO_INTERVALO = `Informe um número inteiro de minutos, de ${String(INTERVALO_MINIMO)} a ${String(INTERVALO_MAXIMO)}.`;

/** O intervalo digitado, validado antes de ir à API (que recusa fora de 1–1440). */
export function intervaloDoTexto(
  texto: string,
): { readonly valor: number } | { readonly erro: string } {
  const limpo = texto.trim();

  if (!/^\d{1,4}$/.test(limpo)) {
    return { erro: ERRO_DO_INTERVALO };
  }

  const valor = Number(limpo);
  return valor >= INTERVALO_MINIMO && valor <= INTERVALO_MAXIMO
    ? { valor }
    : { erro: ERRO_DO_INTERVALO };
}

const ROTULO_DO_TIPO: Readonly<Record<TipoDeLote, string>> = {
  categorias: 'Categorias',
  produtos: 'Produtos',
  saldos: 'Saldos',
};

export function rotuloDoTipoDeLote(tipo: TipoDeLote): string {
  return ROTULO_DO_TIPO[tipo];
}

/** `codPro SKU-1 · codDep ALM01`: os nomes do ERP, para conferir lá. */
export function identificacaoEmTexto(identificacao: Readonly<Record<string, string>>): string {
  // `{ texto }`: a trilha gravou o registro fora do formato `campo=valor`.
  const { texto, ...campos } = identificacao;
  if (texto !== undefined && Object.keys(campos).length === 0) {
    return texto;
  }

  const partes = Object.entries(identificacao).map(([campo, valor]) => `${campo} ${valor}`);
  return partes.length === 0 ? '—' : partes.join(' · ');
}

/** "Todos" no seletor: a chave não vai na consulta (`api/recorte.ts`). */
export const TODOS = '';

export const OPCOES_DE_TIPO: readonly OpcaoFiltro[] = [
  { valor: TODOS, rotulo: 'Tipo · todos' },
  { valor: 'categorias', rotulo: 'Categorias' },
  { valor: 'produtos', rotulo: 'Produtos' },
  { valor: 'saldos', rotulo: 'Saldos' },
];

/**
 * O valor do seletor como recorte do servidor: só um dos `permitidos` vira
 * filtro; "todos" (ou qualquer outra coisa) não recorta.
 */
export function valorDoSeletor<T extends string>(
  valor: string,
  permitidos: readonly T[],
): T | null {
  return (permitidos as readonly string[]).includes(valor) ? (valor as T) : null;
}

export const TIPOS_DE_LOTE: readonly TipoDeLote[] = ['categorias', 'produtos', 'saldos'];
export const STATUS_DO_LOTE = ['Recebido', 'Confirmado'] as const;

/*
 * Situação no ERP. "Todas"/"todos" vai EXPLÍCITO na consulta: sem `situacao`,
 * a API devolve só os ativos, e a tela abre mostrando tudo — ativo, inativo
 * e excluído —, porque a situação de cada linha é o que se veio conferir.
 * "Fora de uso" é `ativo = false`: excluído no ERP ou inativo lá.
 */
export const SITUACOES_DA_CATEGORIA = ['todas', 'ativas', 'excluidas'] as const;
export const SITUACAO_PADRAO_DA_CATEGORIA = 'todas';

export const OPCOES_DE_SITUACAO_DA_CATEGORIA: readonly OpcaoFiltro[] = [
  { valor: 'todas', rotulo: 'Situação · todas' },
  { valor: 'ativas', rotulo: 'Ativas' },
  { valor: 'excluidas', rotulo: 'Fora de uso no ERP' },
];

export const SITUACOES_DO_CADASTRO = ['todos', 'ativos', 'excluidos'] as const;
export const SITUACAO_PADRAO_DO_CADASTRO = 'todos';

export const OPCOES_DE_SITUACAO_DO_CADASTRO: readonly OpcaoFiltro[] = [
  { valor: 'todos', rotulo: 'Situação · todos' },
  { valor: 'ativos', rotulo: 'Ativos' },
  { valor: 'excluidos', rotulo: 'Fora de uso no ERP' },
];
